import * as THREE from 'three';

const textureCache: Record<string, any> = {};

export function createLinenTexture(): { diffuse: THREE.CanvasTexture, bump: THREE.CanvasTexture } {
    if (textureCache['linen']) return textureCache['linen'];

    const size = 512;
    
    // Bump map
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = size;
    bumpCanvas.height = size;
    const bumpCtx = bumpCanvas.getContext('2d')!;
    
    // Diffuse map
    const diffCanvas = document.createElement('canvas');
    diffCanvas.width = size;
    diffCanvas.height = size;
    const diffCtx = diffCanvas.getContext('2d')!;
    
    bumpCtx.fillStyle = '#808080';
    bumpCtx.fillRect(0, 0, size, size);
    
    diffCtx.fillStyle = '#e8e5df'; // Base linen color
    diffCtx.fillRect(0, 0, size, size);
    
    // Draw weave
    // Seeded random replacement for consistent appearance
    let seed = 12345;
    const seededRandom = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
    };

    for (let i = 0; i < 10000; i++) {
        const x = seededRandom() * size;
        const y = seededRandom() * size;
        const length = 5 + seededRandom() * 20;
        const isHorizontal = seededRandom() > 0.5;
        
        // Bump
        const bumpColor = seededRandom() > 0.5 ? '#ffffff' : '#000000';
        bumpCtx.fillStyle = bumpColor;
        bumpCtx.globalAlpha = 0.1;
        
        // Diffuse
        diffCtx.fillStyle = '#000000';
        diffCtx.globalAlpha = 0.05;
        
        if (isHorizontal) {
            bumpCtx.fillRect(x, y, length, 1);
            diffCtx.fillRect(x, y, length, 1);
        } else {
            bumpCtx.fillRect(x, y, 1, length);
            diffCtx.fillRect(x, y, 1, length);
        }
    }
    
    const bumpTex = new THREE.CanvasTexture(bumpCanvas);
    bumpTex.wrapS = THREE.RepeatWrapping;
    bumpTex.wrapT = THREE.RepeatWrapping;
    
    const diffTex = new THREE.CanvasTexture(diffCanvas);
    diffTex.wrapS = THREE.RepeatWrapping;
    diffTex.wrapT = THREE.RepeatWrapping;
    
    textureCache['linen'] = { diffuse: diffTex, bump: bumpTex };
    return textureCache['linen'];
}

export function createVelvetTexture(): THREE.CanvasTexture {
    if (textureCache['velvet']) return textureCache['velvet'];

    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    // Radial gradient for sheen
    const gradient = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
    gradient.addColorStop(0, '#ffffff'); // bright center
    gradient.addColorStop(1, '#888888'); // dark edges
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    
    const tex = new THREE.CanvasTexture(canvas);
    textureCache['velvet'] = tex;
    return tex;
}

export function createSheerPerforationTexture(): THREE.CanvasTexture {
    if (textureCache['sheer']) return textureCache['sheer'];

    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    
    ctx.fillStyle = '#000000';
    for (let y = 0; y < size; y += 4) {
        for (let x = 0; x < size; x += 4) {
            ctx.fillRect(x, y, 2, 2);
        }
    }
    
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    
    textureCache['sheer'] = tex;
    return tex;
}
